import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { STATUS_ARQUIVO } from '../../utils/opcoes';
import Ajuda from './Ajuda';
import Marca from './Marca';
import SelecaoBuscavel from './SelecaoBuscavel';

export { Ajuda, Marca, SelecaoBuscavel };

/* ── Carregando ─────────────────────────────────────────────────────────── */
export function LoadingSpinner({ texto = 'Carregando…' }) {
  return (
    <div className="ff-spinner" role="status" aria-live="polite">
      <div className="ff-spinner__circulo" />
      <span>{texto}</span>
    </div>
  );
}

/* ── Cabeçalho de página ────────────────────────────────────────────────── */
export function PageHeader({ titulo, subtitulo, acoes }) {
  return (
    <div className="ff-page-head">
      <div>
        <h1>{titulo}</h1>
        {subtitulo && <p>{subtitulo}</p>}
      </div>
      {acoes && <div className="ff-painel__acoes">{acoes}</div>}
    </div>
  );
}

/* ── Botão ──────────────────────────────────────────────────────────────── */
export function Botao({
  variante = 'primario',
  pequeno = false,
  icone,
  children,
  className = '',
  ...rest
}) {
  const classes = [
    'ff-btn',
    `ff-btn--${variante}`,
    pequeno ? 'ff-btn--pequeno' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={classes} {...rest}>
      {icone && <i className={`bi ${icone}`} aria-hidden="true" />}
      {children}
    </button>
  );
}

/* ── Campo de formulário ────────────────────────────────────────────────── */
export function Campo({ id, label, erro, ajuda, valor = false, children, ...rest }) {
  return (
    <div className={`ff-campo${erro ? ' ff-campo--erro' : ''}`}>
      {label && <label htmlFor={id}>{label}</label>}
      {children || (
        <input
          id={id}
          className={`ff-input${valor ? ' ff-input--valor' : ''}`}
          aria-invalid={erro ? 'true' : undefined}
          aria-describedby={erro ? `${id}-erro` : undefined}
          {...rest}
        />
      )}
      {erro && (
        <span className="ff-erro-msg" id={`${id}-erro`}>
          {erro}
        </span>
      )}
      {!erro && ajuda && (
        <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>{ajuda}</span>
      )}
    </div>
  );
}

/* ── Badge de status ────────────────────────────────────────────────────── */
export function Badge({ tom = 'arquivado', children }) {
  return <span className={`ff-badge ff-badge--${tom}`}>{children}</span>;
}

export function StatusArquivoBadge({ status }) {
  const cfg = STATUS_ARQUIVO[status] || { label: status, tom: 'arquivado' };
  return <Badge tom={cfg.tom}>{cfg.label}</Badge>;
}

/* ── Card de indicador ──────────────────────────────────────────────────── */
export function Indicador({ rotulo, valor, nota, natureza = 'neutro' }) {
  return (
    <div className={`ff-tile ff-tile--${natureza}`}>
      <span className="rotulo">{rotulo}</span>
      <span className="valor">{valor}</span>
      {nota && <span className="nota">{nota}</span>}
    </div>
  );
}

/* ── Aviso ──────────────────────────────────────────────────────────────── */
const ICONE_AVISO = {
  info: 'bi-info-circle',
  erro: 'bi-exclamation-octagon',
  atencao: 'bi-exclamation-triangle',
  ok: 'bi-check-circle',
};

export function Alerta({ tipo = 'erro', children, onFechar }) {
  if (!children) return null;
  const tom = { danger: 'erro', warning: 'atencao', success: 'ok', info: 'info' }[tipo] || tipo;
  return (
    <div className={`ff-aviso ff-aviso--${tom}`} role="alert">
      <i className={`bi ${ICONE_AVISO[tom] || ICONE_AVISO.info}`} aria-hidden="true" />
      <div>{children}</div>
      {onFechar && (
        <button
          type="button"
          className="ff-aviso__fechar"
          onClick={onFechar}
          aria-label="Fechar aviso"
        >
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/* ── Estado vazio ───────────────────────────────────────────────────────── */
export function Vazio({ icone = 'bi-inbox', titulo, descricao, acao }) {
  return (
    <div className="ff-vazio">
      <i className={`bi ${icone}`} aria-hidden="true" />
      <p className="ff-vazio__titulo">{titulo}</p>
      {descricao && <p className="ff-vazio__desc">{descricao}</p>}
      {acao}
    </div>
  );
}

/* ── Painel com cabeçalho e busca ───────────────────────────────────────── */
export function Painel({ titulo, subtitulo, busca, acoes, children, rodape }) {
  const temCabecalho = titulo || busca || acoes;
  return (
    <section className="ff-painel">
      {temCabecalho && (
        <header className="ff-painel__cabecalho">
          <div>
            {titulo && <h2 className="ff-painel__titulo">{titulo}</h2>}
            {subtitulo && <p className="ff-painel__sub">{subtitulo}</p>}
          </div>
          {(busca || acoes) && (
            <div className="ff-painel__acoes">
              {busca}
              {acoes}
            </div>
          )}
        </header>
      )}
      {children}
      {rodape}
    </section>
  );
}

/* ── Campo de busca ─────────────────────────────────────────────────────── */
export function Busca({ valor, onChange, placeholder = 'Buscar…', id = 'busca' }) {
  return (
    <div className="ff-busca">
      <i className="bi bi-search" aria-hidden="true" />
      <input
        id={id}
        type="search"
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
    </div>
  );
}

/* ── Tabela rolável ─────────────────────────────────────────────────────── */
export function Tabela({ estreita = false, children }) {
  return (
    <div className="ff-tabela-wrap">
      <table className={`ff-tabela${estreita ? ' ff-tabela--estreita' : ''}`}>{children}</table>
    </div>
  );
}

/**
 * Cabeçalho ordenável. `estado` vem de `useOrdenacao`.
 */
export function Th({ campo, estado, alinhar, children }) {
  if (!estado) return <th style={alinhar ? { textAlign: alinhar } : undefined}>{children}</th>;

  const ativo = estado.campo === campo;
  const direcao = ativo ? (estado.asc ? 'ascending' : 'descending') : undefined;
  return (
    <th
      className="ff-th-ord"
      onClick={() => estado.ordenarPor(campo)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          estado.ordenarPor(campo);
        }
      }}
      tabIndex={0}
      role="columnheader"
      aria-sort={direcao}
      style={alinhar ? { textAlign: alinhar } : undefined}
    >
      {children}
      <i
        className={`bi ${ativo ? (estado.asc ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-down-up'}`}
        aria-hidden="true"
      />
    </th>
  );
}

/* ── Rota protegida ─────────────────────────────────────────────────────── */
export function PrivateRoute({ children, somenteAdmin = false, somenteGestor = false }) {
  const { usuario, carregando, isAdmin, isGestor } = useAuth();
  if (carregando) return <LoadingSpinner texto="Verificando sessão…" />;
  if (!usuario) return <Navigate to="/login" replace />;
  // Quem não tem o perfil volta ao painel — a rota não vaza nem o "403".
  if (somenteAdmin && !isAdmin) return <Navigate to="/dashboard" replace />;
  if (somenteGestor && !isGestor) return <Navigate to="/dashboard" replace />;
  return children;
}

export { Paginacao } from './Paginacao';
